import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

const applyTimeMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const COMPLETE_TIME = /^\d{2}:\d{2}$/;
const ALLOWED_CHARS = /^[\d:]*$/;

interface TimeInputProps {
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  className?: string;
  id?: string;
  // Quando true, campo vazio é considerado válido (não pinta borda vermelha).
  allowEmpty?: boolean;
}

// Input mascarado pra MM:SS — espelha o padrão usado em MiniHookConfig.
// - Aceita apenas dígitos (max 4); insere ":" automaticamente após o 2º.
// - Borda vermelha quando o usuário digita caractere fora de [0-9:] ou
//   sai do campo com tempo incompleto.
// - O estado é local; o pai recebe o valor final via `onCommit` (no blur).
export const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onCommit,
  placeholder = '00:00',
  className,
  id,
  allowEmpty = true,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [invalid, setInvalid] = useState(false);

  // Sincroniza com mudanças externas (load inicial, reset).
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInvalid(!ALLOWED_CHARS.test(raw));
    setLocalValue(applyTimeMask(raw));
  };

  const handleBlur = () => {
    const incomplete = localValue !== '' && !COMPLETE_TIME.test(localValue);
    const emptyInvalid = !allowEmpty && localValue === '';
    setInvalid(incomplete || emptyInvalid);
    onCommit(localValue);
  };

  return (
    <Input
      id={id}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      maxLength={5}
      inputMode="numeric"
      className={cn(
        'font-mono text-center',
        invalid &&
          'border-red-500 focus-visible:ring-red-500/20 focus-visible:border-red-500',
        className
      )}
    />
  );
};
